import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

ChartJS.register(...registerables);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable23 = ({ data }) => {
  const pdfRef = useRef();

  // Convert to numeric
  const formattedData = data.map((item) => ({
    ...item,
    asian: Number(item.asian_alone),
    black: Number(item.black_or_african_american_alone),
  }));

  // Extract arrays
  const years = formattedData.map((i) => i.year);
  const asianValues = formattedData.map((i) => i.asian);
  const blackValues = formattedData.map((i) => i.black);

  // Calculations
  const avgAsian = (
    asianValues.reduce((s, v) => s + v, 0) / asianValues.length
  ).toFixed(2);
  const avgBlack = (
    blackValues.reduce((s, v) => s + v, 0) / blackValues.length
  ).toFixed(2);

  const asianMax = formattedData.reduce((a, b) => (a.asian > b.asian ? a : b));
  const blackMax = formattedData.reduce((a, b) => (a.black > b.black ? a : b));

  const asianMin = formattedData.reduce((a, b) => (a.asian < b.asian ? a : b));
  const blackMin = formattedData.reduce((a, b) => (a.black < b.black ? a : b));

  // Chart configuration
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Asian Alone",
        data: asianValues,
        backgroundColor: "#10B98160",
        borderColor: "#10B981",
        borderWidth: 2,
      },
      {
        label: "Black or African American Alone",
        data: blackValues,
        backgroundColor: "#37415160",
        borderColor: "#374151",
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment Comparison (Asian Alone vs Black or African American Alone)",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y: { title: { display: true, text: "Employment" }, beginAtZero: true },
      x: { title: { display: true, text: "Year" } },
    },
  };

  // PDF Export
 const downloadPDF = () => {
   const input = pdfRef.current;
   html2canvas(input, {
     scale: 2,
     useCORS: true,
     backgroundColor: "#ffffff",
     scrollY: -window.scrollY,
   }).then((canvas) => {
     const imgData = canvas.toDataURL("image/png");
     const pdf = new jsPDF("p", "mm", "a4");
     const pageWidth = pdf.internal.pageSize.getWidth();
     const pageHeight = pdf.internal.pageSize.getHeight();
     const imgWidth = pageWidth - 10;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;

     let heightLeft = imgHeight;
     let position = 0;
     let pageNumber = 1;

     const addHeader = () => {
       const logoWidth = 20; // ideal header width
       const logoHeight = 20; // adjust ratio if needed

       pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
       pdf.setFontSize(26);
       pdf.text("UTahData.org", 95, 22);
       pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
       pdf.line(10, 35, 200, 35);
     };

     const addFooter = (pageNumber) => {
       pdf.setFontSize(9);
       const footerY = pageHeight - 10;
       pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         10,
         footerY,
       );
       pdf.text(
         `Page ${pageNumber}`,
         pageWidth - 10,
         footerY,
         null,
         null,
         "right",
       );
     };

     // First page
     addHeader();
     pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
     heightLeft -= pageHeight - 32;

     // Add more pages if needed
     while (heightLeft > 0) {
       pdf.addPage();
       pageNumber++;
       addHeader();
       position = heightLeft - imgHeight;
       pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;
       addFooter(pageNumber);
     }

     // Final footer
     // ===== FINAL FOOTER SAFE AREA =====

     const footerTopY = pageHeight - 40; // start footer 40mm from bottom
     const footerContentY = pageHeight - 28; // logos row
     const footerBottomTextY = pageHeight - 10; // bottom text row

     // Top Divider Line
     pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

     // UtahData logo
     const utahWidth = 18;
     const utahHeight = 18;
     pdf.addImage(
       utahData,
       "PNG",
       20,
       footerContentY - 8,
       utahWidth,
       utahHeight,
     );

     // Built by
     pdf.setFontSize(10);
     pdf.text("Built by", 45, footerContentY);

     // BODEX logo
     const bodexWidth = 36;
     const bodexHeight = bodexWidth * 0.3;
     pdf.addImage(
       bodex,
       "PNG",
       60,
       footerContentY - 6,
       bodexWidth,
       bodexHeight,
     );

     // Using text
     pdf.text("using", 102, footerContentY);

     // KPI logo
     pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

     // Version
     pdf.text(APP_VERSION, 140, footerContentY);

     // Bottom Info Line
     pdf.setFontSize(8);
     pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
     pdf.text(
       `Downloaded on: ${new Date().toLocaleDateString()}`,
       pageWidth - 10,
       footerBottomTextY,
       null,
       null,
       "right",
     );

     pdf.save("EngTable23_Comparison.pdf");
   });
 };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-xl">
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
          Employment Comparison: Asian vs Black or African American
        </h2>

        {/* Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Asian Insights */}
          <div className="bg-blue-50 rounded-lg p-4 shadow-lg">
            <p className="font-semibold text-blue-800 text-lg">
              Asian Alone 🟦
            </p>
            <p>
              📈 Highest: {asianMax.asian} (Year {asianMax.year})
            </p>
            <p>
              📉 Lowest: {asianMin.asian} (Year {asianMin.year})
            </p>
            <p>📊 Average: {avgAsian}</p>
          </div>

          {/* Black Insights */}
          <div className="bg-red-50 rounded-lg p-4 shadow-lg">
            <p className="font-semibold text-red-800 text-lg">
              Black or African American 🟥
            </p>
            <p>
              📈 Highest: {blackMax.black} (Year {blackMax.year})
            </p>
            <p>
              📉 Lowest: {blackMin.black} (Year {blackMin.year})
            </p>
            <p>📊 Average: {avgBlack}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full text-center shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    <th className="p-2">Asian Alone</th>
                    <th className="p-2">Black or African American Alone</th>
                  </tr>
                </thead>
                <tbody>
                  {formattedData.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100">
                      <td className="p-2 font-semibold">{row.year}</td>
                      <td className="p-2">
                        {row.asian.toLocaleString("en-US")}
                      </td>
                      <td className="p-2">
                        {row.black.toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable23;

